<template>
  <head>
    <meta charset="utf-8" />
    <title>NTSA | User Registration</title>
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <meta content="Keep your finances and membership up to date" name="description" />
    <meta content="" name="author" />
    <!-- App favicon -->
    <link rel="shortcut icon" href="assets/images/favicon.svg">

    <!-- DataTables -->
    <link href="assets/libs/datatables.net-bs4/css/dataTables.bootstrap4.min.css" rel="stylesheet" type="text/css" />
    <link href="assets/libs/datatables.net-buttons-bs4/css/buttons.bootstrap4.min.css" rel="stylesheet" type="text/css" />

    <!-- Bootstrap Css -->
    <link href="assets/css/bootstrap.min.css" id="bootstrap-style" rel="stylesheet" type="text/css" />
    <!-- Bootstrap select styling -->
    <link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/bootstrap-select@1.13.14/dist/css/bootstrap-select.min.css">
    <!-- Icons Css -->
    <link href="assets/css/icons.min.css" rel="stylesheet" type="text/css" />
    <!-- App Css-->
    <link href="assets/css/app.min.css" id="app-style" rel="stylesheet" type="text/css" />
    <!-- custom Css-->
    <link href="assets/css/custom.css" id="app-style" rel="stylesheet" type="text/css" />
  </head>

  <body @contextmenu.prevent data-sidebar="dark">
  <!-- Begin page -->
  <div id="layout-wrapper">
    <NavigationBar/>

    <!-- Start right Content here -->
    <div id="content" class="content-minimize">
      <div class="page-content">
        <div class="container-fluid">

          <!-- start page title -->
          <div class="row">
            <div class="col-12">
              <div class="page-title-box d-sm-flex align-items-center justify-content-between">
                <h4 class="mb-sm-0 font-size-18">User Registration</h4>

                <div class="page-title-right">
                  <ol class="breadcrumb m-0">
                    <li class="breadcrumb-item"><a @click="gotTo('dashboard')" style="cursor: pointer;">Dashboards</a></li>
                    <li class="breadcrumb-item active">User Registration</li>
                  </ol>
                </div>
              </div>
            </div>
          </div>
          <!-- end page title -->

          <div class="row">
            <div class="col-12">
              <div class="card">
                <div class="card-body">
                  <!-- Header Section -->
                  <div class="mb-4">
                    <h4 class="card-title mb-2">User Registration</h4>
                    <p class="text-muted">
                      Complete the form below to register a new user. The user can login using either their <strong>employment number</strong> or <strong>ID number</strong>. An OTP will be sent to the user's phone number for their first login.
                      <span class="text-danger">*</span> indicates required fields.
                    </p>
                  </div>

                  <!-- Alert Messages -->
                  <div v-if="alert.success" class="alert alert-success alert-dismissible fade show" role="alert">
                    <i class="mdi mdi-check-circle me-2"></i>{{ message }}
                    <button type="button" class="btn-close" @click="alert.success = false"></button>
                  </div>

                  <div v-if="alert.info" class="alert alert-info alert-dismissible fade show" role="alert">
                    <i class="mdi mdi-information me-2"></i>{{ message }}
                    <button type="button" class="btn-close" @click="alert.info = false"></button>
                  </div>

                  <div v-if="alert.failed" class="alert alert-danger alert-dismissible fade show" role="alert">
                    <i class="mdi mdi-alert-circle me-2"></i>{{ message }}
                    <button type="button" class="btn-close" @click="alert.failed = false"></button>
                  </div>

                  <!-- Registration Form -->
                  <form @submit.prevent="createUser">
                    <!-- Personal Information Section -->
                    <div class="mb-4">
                      <h5 class="text-primary mb-3">
                        <i class="mdi mdi-account-circle me-2"></i>Personal Information
                      </h5>
                      <div class="row g-3">
                        <!-- Full Name -->
                        <div class="col-md-6">
                          <label for="names" class="form-label">
                            Full Name <span class="text-danger">*</span>
                          </label>
                          <input
                              v-model="names"
                              @input="validateInput"
                              type="text"
                              class="form-control"
                              :class="{'is-invalid': !isValid && names.length > 0}"
                              id="names"
                              placeholder="e.g., John Doe"
                              required
                          >
                          <div v-if="!isValid && names.length > 0" class="invalid-feedback">
                            Please enter a valid name (letters and spaces only)
                          </div>
                        </div>

                        <!-- ID Number -->
                        <div class="col-md-6">
                          <label for="idNo" class="form-label">
                            Employment Number / ID Number <span class="text-danger">*</span>
                          </label>
                          <input
                              v-model="idNo"
                              @input="sanitizeIdNo"
                              type="text"
                              class="form-control"
                              id="idNo"
                              placeholder="Enter employment number or ID number"
                              required
                          >
                          <small class="form-text text-muted">User can login with either their employment number or national ID number</small>
                        </div>

                        <!-- Gender -->
                        <div class="col-12">
                          <label class="form-label">
                            Gender <span class="text-danger">*</span>
                          </label>
                          <div class="d-flex gap-4">
                            <div class="form-check">
                              <input
                                  v-model="gender"
                                  value="Male"
                                  class="form-check-input"
                                  type="radio"
                                  name="gender"
                                  id="genderMale"
                              >
                              <label class="form-check-label" for="genderMale">
                                <i class="mdi mdi-gender-male me-1"></i>Male
                              </label>
                            </div>
                            <div class="form-check">
                              <input
                                  v-model="gender"
                                  value="Female"
                                  class="form-check-input"
                                  type="radio"
                                  name="gender"
                                  id="genderFemale"
                              >
                              <label class="form-check-label" for="genderFemale">
                                <i class="mdi mdi-gender-female me-1"></i>Female
                              </label>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- Contact Information Section -->
                    <div class="mb-4">
                      <h5 class="text-primary mb-3">
                        <i class="mdi mdi-phone-in-talk me-2"></i>Contact Information
                      </h5>
                      <div class="row g-3">
                        <!-- Phone Number -->
                        <div class="col-md-6">
                          <label for="phoneNumber" class="form-label">
                            Phone Number <span class="text-danger">*</span>
                          </label>
                          <input
                              v-model="phoneNumber"
                              @input="sanitizeAndValidatePhoneNumber"
                              type="tel"
                              class="form-control"
                              :class="{'is-invalid': !isPhoneNumberValid && phoneNumber.length > 0}"
                              id="phoneNumber"
                              placeholder="e.g., 0712345678"
                              maxlength="10"
                              required
                          >
                          <div v-if="!isPhoneNumberValid && phoneNumber.length > 0" class="invalid-feedback">
                            Please enter a valid 10-digit phone number
                          </div>
                          <small class="form-text text-muted">OTP will be sent to this number</small>
                        </div>

                        <!-- Email -->
                        <div class="col-md-6">
                          <label for="email" class="form-label">
                            Email Address <span class="text-danger">*</span>
                          </label>
                          <input
                              v-model="email"
                              @input="validateEmail"
                              type="email"
                              class="form-control"
                              :class="{'is-invalid': !isEmailValid && email.length > 0}"
                              id="email"
                              placeholder="e.g., john.doe@example.com"
                              required
                          >
                          <div v-if="!isEmailValid && email.length > 0" class="invalid-feedback">
                            Please enter a valid email address
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- System & Role Assignment Section -->
                    <div class="mb-4">
                      <h5 class="text-primary mb-3">
                        <i class="mdi mdi-shield-account me-2"></i>System & Role Assignment
                      </h5>
                      <div class="row g-3">
                        <!-- System Selection -->
                        <div class="col-md-4">
                          <label for="system" class="form-label">
                            System <span class="text-danger">*</span>
                          </label>
                          <select
                              class="form-select"
                              id="system"
                              v-model="selectedSystem"
                              @change="handleSystemChange"
                              required
                          >
                            <option :value="null" disabled>Select System</option>
                            <option v-for="system in systems" :key="system.id" :value="system">
                              {{ system.name }}
                            </option>
                          </select>
                          <small class="form-text text-muted">Select the system this user will access</small>
                        </div>

                        <!-- Category -->
                        <div class="col-md-4">
                          <label for="category" class="form-label">
                            Category/Role <span class="text-danger">*</span>
                          </label>
                          <select
                              class="form-select"
                              v-model="selectedCategory"
                              id="category"
                              :disabled="!selectedSystem"
                              required
                          >
                            <option value="" disabled>Select Category</option>
                            <option v-for="(item, index) in filteredCategories" :key="index" :value="item.category">
                              {{ item.category }}
                            </option>
                          </select>
                          <small v-if="!selectedSystem" class="form-text text-muted">Select a system first</small>
                        </div>

                        <!-- Facility Access Type (Hospital only) -->
                        <div v-if="isHospitalSystem" class="col-md-4">
                          <label for="facilityAccessType" class="form-label">
                            Facility Access <span class="text-danger">*</span>
                          </label>
                          <select
                              class="form-select"
                              v-model="facilityAccessType"
                              id="facilityAccessType"
                              @change="handleFacilityAccessTypeChange"
                              required
                          >
                            <option value="single">Single Facility</option>
                            <option value="multiple">Multiple Facilities</option>
                            <option value="all">All Facilities</option>
                          </select>
                        </div>
                      </div>

                      <!-- Facility Selection (Hospital only) -->
                      <div v-if="isHospitalSystem" class="row g-3 mt-2">
                        <!-- Primary Facility -->
                        <div v-if="facilityAccessType !== 'all'" class="col-md-6">
                          <label for="primaryFacility" class="form-label">
                            {{ facilityAccessType === 'single' ? 'Facility' : 'Primary Facility' }} <span class="text-danger">*</span>
                          </label>
                          <select
                              class="form-select"
                              v-model="selectedFacility"
                              id="primaryFacility"
                              required
                          >
                            <option :value="null" disabled>Select Facility</option>
                            <option v-for="facility in facilities" :key="facility.facilityID" :value="facility">
                              {{ facility.facilityName }} ({{ facility.facilityCode }})
                            </option>
                          </select>
                        </div>

                        <!-- Additional Facilities (Multiple only) -->
                        <div v-if="facilityAccessType === 'multiple'" class="col-md-6">
                          <label class="form-label">Additional Facilities</label>
                          <div class="border rounded p-2" style="max-height: 150px; overflow-y: auto;">
                            <div v-for="facility in facilities" :key="facility.facilityID" class="form-check">
                              <input
                                  type="checkbox"
                                  class="form-check-input"
                                  :id="'facility_' + facility.facilityID"
                                  :value="facility.facilityID"
                                  v-model="additionalFacilities"
                                  :disabled="selectedFacility && selectedFacility.facilityID === facility.facilityID"
                              >
                              <label class="form-check-label" :for="'facility_' + facility.facilityID">
                                {{ facility.facilityName }}
                                <span v-if="selectedFacility && selectedFacility.facilityID === facility.facilityID" class="badge bg-primary ms-1">Primary</span>
                              </label>
                            </div>
                          </div>
                          <small class="form-text text-muted">Select additional facilities this user can access</small>
                        </div>

                        <!-- All Facilities Info -->
                        <div v-if="facilityAccessType === 'all'" class="col-12">
                          <div class="alert alert-info mb-0">
                            <i class="mdi mdi-information me-2"></i>
                            This user will have access to <strong>all {{ facilities.length }} facilities</strong> in the system.
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- Location Information Section -->
                    <div class="mb-4">
                      <h5 class="text-primary mb-3">
                        <i class="mdi mdi-map-marker me-2"></i>Location & Assignment
                      </h5>
                      <div class="row g-3">
                        <!-- Subcounty -->
                        <div class="col-md-4">
                          <label for="subcounty" class="form-label">
                            Sub-County <span class="text-danger">*</span>
                          </label>
                          <select
                              class="form-select"
                              id="subcounty"
                              @change="handleSubCountyChange"
                              v-model="subCounty"
                              required
                          >
                            <option :value="null" disabled>Select Sub-County</option>
                            <option v-for="(item, index) in subCounties" :key="index" :value="item">
                              {{ item.subCountyName }}
                            </option>
                          </select>
                        </div>

                        <!-- Ward -->
                        <div class="col-md-4">
                          <label for="ward" class="form-label">
                            Ward <span class="text-danger">*</span>
                          </label>
                          <select
                              class="form-select"
                              v-model="ward"
                              @change="handleWardChange"
                              id="ward"
                              :disabled="!subCounty"
                              required
                          >
                            <option :value="null" disabled>Select Ward</option>
                            <option v-for="(item, index) in wards" :key="index" :value="item">
                              {{ item.wardName }}
                            </option>
                          </select>
                          <small v-if="!subCounty" class="form-text text-muted">Select a sub-county first</small>
                        </div>

                        <!-- Zone -->
                        <div class="col-md-4">
                          <label for="zone" class="form-label">
                            Zone <span v-if="!isHospitalSystem" class="text-danger">*</span>
                          </label>
                          <select
                              class="form-select"
                              v-model="zone"
                              id="zone"
                              :disabled="!ward || zones.length === 0"
                              :required="!isHospitalSystem"
                          >
                            <option value="">Select Zone</option>
                            <option v-for="(item, index) in zones" :key="index" :value="item.zone">
                              {{ item.zone }}
                            </option>
                          </select>
                          <small v-if="!ward" class="form-text text-muted">Select a ward first</small>
                          <small v-else-if="zones.length === 0" class="form-text text-warning">No zones available for this ward</small>
                        </div>
                      </div>
                    </div>

                    <!-- Security Settings Section -->
                    <div class="mb-4">
                      <h5 class="text-primary mb-3">
                        <i class="mdi mdi-security me-2"></i>Security Settings
                      </h5>
                      <div class="row g-3">
                        <!-- Password (if applicable) -->
                        <div v-if="getRight('CREATE USER PASSWORD')" class="col-md-6">
                          <label for="password" class="form-label">
                            Password (4 digits)
                          </label>
                          <input
                              v-model="password"
                              type="password"
                              maxlength="4"
                              class="form-control"
                              id="password"
                              placeholder="Enter 4-digit PIN (optional)"
                              @input="password = password.replace(/[^0-9]/g, '')"
                          >
                          <small class="form-text text-muted">Leave blank to auto-generate. Numeric characters only (0-9)</small>
                        </div>

                        <!-- OTP Requirement -->
                        <div class="col-12">
                          <div class="form-check form-switch">
                            <input
                                type="checkbox"
                                class="form-check-input"
                                id="requireOTP"
                                v-model="requireOTP"
                            >
                            <label class="form-check-label" for="requireOTP">
                              <strong>Require OTP for login</strong>
                              <br>
                              <small class="text-muted">When enabled, user will receive an OTP on their phone for login</small>
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- Action Buttons -->
                    <div class="border-top pt-4">
                      <div class="d-flex gap-2">
                        <button
                            v-if="getRight('CREATE USER')"
                            type="submit"
                            class="btn btn-primary"
                            :disabled="!isFormValid || alert.info"
                        >
                          <i class="mdi mdi-account-plus-outline me-1"></i>
                          {{ alert.info ? 'Processing...' : 'Register User' }}
                        </button>
                        <button
                            type="button"
                            class="btn btn-secondary"
                            @click="resetForm"
                        >
                          <i class="mdi mdi-refresh me-1"></i>Clear Form
                        </button>
                      </div>
                    </div>
                  </form>

                </div>
              </div>
            </div>
          </div>

        </div>
      </div>

      <footer class="footer">
        <div class="container-fluid">
          <div class="row">
            <div class="col-sm-6">
              © Best Birdy
            </div>
            <div class="col-sm-6">
              <div class="text-sm-end d-sm-block">
                Best Birdy Billing system.
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  </div>
  </body>
</template>

<script>
/* eslint-disable */
import NavigationBar from "@/components/Navigation";
import {authUrl, biller, execute, getRight, hospital} from "@/api";

export default {
  name: "AddUser",
  components: {NavigationBar},
  data() {
    return {
      requireOTP: true,
      password: '',
      isPhoneNumberValid: true,
      isEmailValid: true,
      isValid: true,
      ward: null,
      subCounty: null,
      gender: 'Female',
      names: '',
      phoneNumber: '',
      idNo: '',
      email: '',
      categories: [],
      selectedCategory: '',
      message: null,
      zones: [],
      zone: '',
      subCounties: [],
      wards: [],
      alert: {
        success: false,
        info: false,
        failed: false
      },
      // System & Facility fields
      systems: [],
      selectedSystem: null,
      facilities: [],
      selectedFacility: null,
      facilityAccessType: 'single',
      additionalFacilities: []
    }
  },
  computed: {
    isFormValid() {
      let baseValid = this.names &&
          this.isValid &&
          this.idNo &&
          this.phoneNumber &&
          this.isPhoneNumberValid &&
          this.email &&
          this.isEmailValid &&
          this.gender &&
          this.subCounty &&
          this.ward &&
          this.selectedSystem &&
          this.selectedCategory;

      // Zone required for non-hospital users
      if (!this.isHospitalSystem) {
        baseValid = baseValid && this.zone;
      }

      // Facility required for hospital users (unless "all" access)
      if (this.isHospitalSystem && this.facilityAccessType !== 'all') {
        baseValid = baseValid && this.selectedFacility;
      }

      return baseValid;
    },
    isHospitalSystem() {
      return this.selectedSystem && this.selectedSystem.code === 'HOSPITAL';
    },
    filteredCategories() {
      if (!this.selectedSystem) return [];
      return this.categories.filter(cat => {
        // Filter categories by systemID
        return cat.systemID == this.selectedSystem.id || !cat.systemID;
      });
    }
  },
  mounted() {
    this.getSystems();
    this.getSubCounties();
    this.getCategories();
    this.menuAccess();
  },
  methods: {
    menuAccess() {
      const data = new FormData();
      data.append("function", "menuAccess");
      data.append("menu", "Add User");
      data.append("sessionNames", sessionStorage.getItem("sessionNames"));
      data.append("sessionIdNo", sessionStorage.getItem("sessionIdNo"));
      execute(data, biller)
          .then(() => {})
          .catch(() => {});
    },

    // Sanitize ID Number - remove all spaces
    sanitizeIdNo() {
      this.idNo = this.idNo.replace(/\s/g, '');
    },

    // Sanitize and validate phone number - remove all spaces and non-digits
    sanitizeAndValidatePhoneNumber() {
      this.phoneNumber = this.phoneNumber.replace(/\s/g, '').replace(/[^0-9]/g, '');
      this.validatePhoneNumber();
    },

    validatePhoneNumber() {
      const phoneRegex = /^\d{10}$/;
      this.isPhoneNumberValid = phoneRegex.test(this.phoneNumber);
    },

    validateEmail() {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      this.isEmailValid = emailRegex.test(this.email);
    },

    validateInput() {
      const regex = /^[a-zA-Z\s]*$/;
      this.isValid = regex.test(this.names);
    },

    getRight(type) {
      return getRight(type);
    },

    handleSubCountyChange() {
      this.ward = null;
      this.zone = '';
      this.zones = [];
      this.wards = [];
      if (this.subCounty && this.subCounty.subCountyID) {
        this.getWards(this.subCounty.subCountyID);
      }
    },

    handleWardChange() {
      this.zone = '';
      this.zones = [];
      if (this.ward && this.ward.wardID) {
        this.getZones(this.ward.wardID);
      }
    },

    handleSystemChange() {
      this.selectedCategory = '';
      this.selectedFacility = null;
      this.additionalFacilities = [];
      this.facilityAccessType = 'single';

      // Load facilities if hospital system selected
      if (this.isHospitalSystem) {
        this.getFacilities();
      }

      // Reload categories filtered by system
      this.getCategoriesBySystem();
    },

    handleFacilityAccessTypeChange() {
      if (this.facilityAccessType === 'all') {
        this.selectedFacility = null;
        this.additionalFacilities = [];
      } else if (this.facilityAccessType === 'single') {
        this.additionalFacilities = [];
      }
    },

    getSystems() {
      const data = new FormData();
      data.append("function", "getSystems");
      execute(data, authUrl)
          .then((res) => {
            if (res.data.success) {
              this.systems = res.data.data.systems || [];
            }
          })
          .catch((e) => {
            console.error("Error loading systems:", e.message);
          });
    },

    getFacilities() {
      const data = new FormData();
      data.append("function", "getFacilities");
      data.append("status", "Active");
      execute(data, hospital)
          .then((res) => {
            if (res.data.success) {
              this.facilities = res.data.data.facilities || [];
            }
          })
          .catch((e) => {
            console.error("Error loading facilities:", e.message);
          });
    },

    getWards(subCountyID) {
      const data = new FormData();
      data.append("function", "getWards");
      data.append("subCountyID", subCountyID);
      execute(data, authUrl)
          .then((res) => {
            if (res.data.success) {
              this.wards = res.data.data.wards;
            } else {
              this.showAlert('failed', res.data.message);
            }
          })
          .catch((e) => {
            this.showAlert('failed', e.message);
          });
    },

    getSubCounties() {
      const data = new FormData();
      data.append("function", "getSubCounties");
      execute(data, authUrl)
          .then((res) => {
            if (res.data.success) {
              this.subCounties = res.data.data.subCounties;
            } else {
              this.showAlert('failed', res.data.message);
            }
          })
          .catch((e) => {
            this.showAlert('failed', e.message);
          });
    },

    getCategories() {
      const data = new FormData();
      data.append("function", "getCategories");
      execute(data, authUrl)
          .then((res) => {
            if (res.data.success) {
              this.categories = res.data.data.categories;
            } else {
              this.showAlert('failed', res.data.message);
            }
          })
          .catch((e) => {
            this.showAlert('failed', e.message);
          });
    },

    getCategoriesBySystem() {
      if (!this.selectedSystem) return;

      const data = new FormData();
      data.append("function", "getCategoriesBySystem");
      data.append("systemID", this.selectedSystem.id);
      execute(data, authUrl)
          .then((res) => {
            if (res.data.success) {
              this.categories = res.data.data.categories || [];
            }
          })
          .catch((e) => {
            console.error("Error loading categories:", e.message);
          });
    },

    getZones(wardID) {
      const data = new FormData();
      data.append("function", "getZones");
      data.append("wardID", wardID);
      execute(data, authUrl)
          .then((res) => {
            if (res.data.success) {
              this.zones = res.data.data.zones;
            } else {
              this.showAlert('failed', res.data.message);
            }
          })
          .catch((e) => {
            this.showAlert('failed', e.message);
          });
    },

    createUser() {
      // Show processing message
      this.showAlert('info', 'Please wait, processing your request...');

      // Prepare form data - strip spaces from idNo and phoneNumber before submitting
      const cleanIdNo = this.idNo.replace(/\s/g, '').trim();
      const cleanPhoneNumber = this.phoneNumber.replace(/\s/g, '').trim();

      const data = new FormData();
      data.append("function", "createUser");
      data.append("names", this.names.trim());
      data.append("gender", this.gender);
      data.append("requireOTP", this.requireOTP);
      data.append("email", this.email.trim());
      data.append("phoneNumber", cleanPhoneNumber);
      data.append("category", this.selectedCategory);
      data.append("idNo", cleanIdNo);
      data.append("zone", this.zone || '');
      data.append("subCountyID", this.subCounty ? this.subCounty.subCountyID : '');
      data.append("subCountyName", this.subCounty ? this.subCounty.subCountyName : '');
      data.append("wardID", this.ward ? this.ward.wardID : '');
      data.append("wardName", this.ward ? this.ward.wardName : '');

      // System ID
      data.append("systemID", this.selectedSystem ? this.selectedSystem.id : '');

      // Facility assignment for hospital users
      if (this.isHospitalSystem) {
        if (this.facilityAccessType === 'all') {
          data.append("facilityID", '');
          data.append("facilityAccess", 'ALL');
        } else if (this.facilityAccessType === 'single') {
          data.append("facilityID", this.selectedFacility ? this.selectedFacility.facilityID : '');
          data.append("facilityAccess", '');
        } else if (this.facilityAccessType === 'multiple') {
          data.append("facilityID", this.selectedFacility ? this.selectedFacility.facilityID : '');
          // Build facility access array including primary facility
          let allFacilities = [...this.additionalFacilities];
          if (this.selectedFacility && !allFacilities.includes(this.selectedFacility.facilityID)) {
            allFacilities.unshift(this.selectedFacility.facilityID);
          }
          data.append("facilityAccess", JSON.stringify(allFacilities));
        }
      }

      if (this.password) {
        data.append("password", btoa(this.password));
      } else {
        data.append("password", "");
      }

      data.append("sessionNames", sessionStorage.getItem("sessionNames"));
      data.append("sessionIdNo", sessionStorage.getItem("sessionIdNo"));

      execute(data, authUrl)
          .then((res) => {
            if (res.data.success) {
              this.showAlert('success', res.data.message);
              this.resetForm();

              // Optionally redirect after successful registration
              setTimeout(() => {
                // this.$router.push('users');
              }, 2000);
            } else {
              this.showAlert('failed', res.data.message);
            }
          })
          .catch((e) => {
            this.showAlert('failed', e.message || 'An error occurred while registering the user');
          });
    },

    resetForm() {
      this.names = '';
      this.email = '';
      this.phoneNumber = '';
      this.idNo = '';
      this.gender = 'Female';
      this.password = '';
      this.subCounty = null;
      this.ward = null;
      this.zone = '';
      this.selectedCategory = '';
      this.selectedSystem = null;
      this.selectedFacility = null;
      this.facilityAccessType = 'single';
      this.additionalFacilities = [];
      this.requireOTP = true;
      this.zones = [];
      this.wards = [];
      this.isValid = true;
      this.isPhoneNumberValid = true;
      this.isEmailValid = true;
    },

    showAlert(type, message) {
      this.alert.success = type === 'success';
      this.alert.info = type === 'info';
      this.alert.failed = type === 'failed';
      this.message = message;

      // Auto-hide success messages after 5 seconds
      if (type === 'success') {
        setTimeout(() => {
          this.alert.success = false;
        }, 5000);
      }
    },

    gotTo(route) {
      this.$router.push(route);
    }
  }
}
</script>

<style scoped>
.form-label {
  font-weight: 500;
  margin-bottom: 0.5rem;
}

.form-select:disabled,
.form-control:disabled {
  background-color: #f8f9fa;
  cursor: not-allowed;
}

.text-primary {
  color: #0d6efd !important;
}

.border-top {
  border-top: 1px solid #dee2e6 !important;
}

.gap-4 {
  gap: 1.5rem !important;
}

.gap-2 {
  gap: 0.5rem !important;
}

.g-3 {
  --bs-gutter-x: 1rem;
  --bs-gutter-y: 1rem;
}

.alert {
  border-radius: 0.375rem;
}

.form-check-input:checked {
  background-color: #0d6efd;
  border-color: #0d6efd;
}

.btn:disabled {
  cursor: not-allowed;
  opacity: 0.65;
}

small.text-warning {
  color: #ffc107 !important;
}

.form-switch .form-check-input {
  width: 3em;
  height: 1.5em;
}

.border.rounded {
  background-color: #f8f9fa;
}

.badge {
  font-size: 0.7rem;
}
</style>